import React, { useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import {
  UserRound,
  MapPin,
  Save,
  CheckCircle2,
  Plus,
  Trash2,
  Edit2,
  Heart,
  Star,
  Ticket,
  CreditCard,
  ShoppingBag,
  Home,
  Briefcase,
  Copy,
  Check
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useToast } from '../hooks/use-toast';
import { authApi } from '../services/api';
import UserPanelShell from '../components/UserPanelShell';

const fmt = (n) => '₹ ' + Number(n || 0).toLocaleString('en-IN');

const sampleWishlist = [
  {
    id: 'p1',
    name: 'Alexandria 3 Seater Leather Sofa - Silver Grey',
    price: 54990,
    marketPrice: 98990,
    discount: 44,
    rating: 5.0,
    reviews: 10,
    stock: 12,
    image: 'https://images.unsplash.com/photo-1555041469-a586c61ea9bc?auto=format&fit=crop&w=600&q=80',
  },
  {
    id: 'p7',
    name: 'Symphony King Box Storage Bed - Acacia Wood',
    price: 34990,
    marketPrice: 59980,
    discount: 41,
    rating: 5.0,
    reviews: 25,
    stock: 5,
    image: 'https://images.unsplash.com/photo-1505693416388-ac5ce068fe85?auto=format&fit=crop&w=600&q=80',
  },
];

const sampleCoupons = [
  { code: 'FOSEN10', title: '10% OFF on all Furniture', discount: '10% OFF', validTill: '31 Dec 2026', minSpend: 15000 },
  { code: 'WELCOME500', title: 'Flat ₹500 OFF on First Order', discount: '₹500 OFF', validTill: '31 Dec 2026', minSpend: 5000 },
  { code: 'LUXURY2026', title: '15% Extra OFF on Solid Wood Beds & Sofas', discount: '15% OFF', validTill: '30 Nov 2026', minSpend: 35000 },
];

const sampleReviews = [
  {
    id: 1,
    productName: 'Alexandria 3 Seater Leather Sofa',
    rating: 5,
    date: '14 Jul 2026',
    comment: 'Exceptional top-grain leather finish. Assembly was smooth and prompt delivery!',
  },
  {
    id: 2,
    productName: 'Symphony King Box Storage Bed',
    rating: 5,
    date: '02 Jun 2026',
    comment: 'Sturdy acacia wood build. Storage space is huge and termite resistant.',
  },
];

const ProfilePage = () => {
  const { user, updateLocalUser } = useAuth();
  const { addToCart } = useCart();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentTab = searchParams.get('tab') || 'profile';

  const [addresses, setAddresses] = useState(user?.addresses || [
    {
      _id: 'addr_1',
      type: 'HOME',
      fullAddress: 'Flat 402, Royal Residency, Indiranagar 100ft Road',
      city: 'Bengaluru',
      state: 'Karnataka',
      pincode: '560038',
      landmark: 'Near Metro Station',
      isDefault: true,
      phone: user?.phone || '9876543210',
    }
  ]);
  const [profile, setProfile] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    gender: user?.gender || 'Male',
    profileImage: user?.profileImage || '',
  });
  const [addressForm, setAddressForm] = useState({
    type: 'HOME',
    fullAddress: '',
    city: '',
    state: '',
    pincode: '',
    landmark: '',
    phone: '',
    isDefault: false
  });
  const [panInfo, setPanInfo] = useState({
    panNumber: user?.panNumber || '',
    fullNameOnPan: user?.fullNameOnPan || user?.name || '',
    gstin: user?.gstin || ''
  });
  const [wishlist, setWishlist] = useState(sampleWishlist);
  const [showAddAddress, setShowAddAddress] = useState(false);
  const [copiedCode, setCopiedCode] = useState('');
  const [message, setMessage] = useState('');

  if (!user) return <Navigate to="/login" replace />;

  const handleTabChange = (tab) => {
    setSearchParams({ tab });
  };

  const copyCouponCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    toast({ title: 'Coupon Code Copied!', description: `Use code ${code} at checkout.` });
    setTimeout(() => setCopiedCode(''), 3000);
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setMessage('');
    try {
      const response = await authApi.updateProfile(profile);
      const nextUser = response.data?.data;
      updateLocalUser(nextUser || { ...user, ...profile });
      toast({ title: 'Profile Updated', description: 'Personal details saved successfully.' });
      setMessage('Profile updated successfully.');
    } catch (error) {
      updateLocalUser({ ...user, ...profile });
      toast({ title: 'Profile Saved', description: 'Saved local settings.' });
    }
  };

  const saveAddress = async (e) => {
    e.preventDefault();
    const newAddr = {
      _id: `addr_${Date.now()}`,
      ...addressForm
    };
    const nextAddresses = [...addresses, newAddr];
    setAddresses(nextAddresses);
    setShowAddAddress(false);
    setAddressForm({ type: 'HOME', fullAddress: '', city: '', state: '', pincode: '', landmark: '', phone: '', isDefault: false });
    toast({ title: 'Address Added', description: 'New delivery address saved.' });
  };

  const deleteAddress = (id) => {
    setAddresses((prev) => prev.filter((a) => a._id !== id));
    toast({ title: 'Address Deleted' });
  };

  const savePanDetails = (e) => {
    e.preventDefault();
    updateLocalUser({ ...user, ...panInfo });
    toast({ title: 'Tax Details Saved', description: 'PAN & GST details updated.' });
  };

  const tabTitles = {
    profile: { title: 'Personal Information', sub: 'Manage your name, email, phone number, and gender preferences.' },
    addresses: { title: 'Manage Delivery Addresses', sub: 'Save and manage delivery locations for fast furniture checkout.' },
    wishlist: { title: 'My Wishlist', sub: 'Saved furniture pieces ready to move to cart anytime.' },
    reviews: { title: 'My Ratings & Reviews', sub: 'Ratings and feedback submitted for your purchased furniture.' },
    coupons: { title: 'Coupons & Discount Offers', sub: 'Exclusive coupon codes available for your next order.' },
    pan: { title: 'PAN & GST Information', sub: 'Save PAN Card & GSTIN for business invoices and GST credit.' },
  };

  const currentInfo = tabTitles[currentTab] || tabTitles.profile;

  return (
    <UserPanelShell title={currentInfo.title} subtitle={currentInfo.sub}>
      {/* Flipkart Style Tab Controls (Mobile Horizontal Scrollable) */}
      <div className="flex gap-2 overflow-x-auto pb-3 mb-6 scrollbar-none">
        {[
          { key: 'profile', label: 'Personal Info', icon: UserRound },
          { key: 'addresses', label: 'Addresses', icon: MapPin },
          { key: 'wishlist', label: 'Wishlist', icon: Heart },
          { key: 'reviews', label: 'My Reviews', icon: Star },
          { key: 'coupons', label: 'Coupons', icon: Ticket },
          { key: 'pan', label: 'PAN / GST', icon: CreditCard },
        ].map((tab) => {
          const active = currentTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => handleTabChange(tab.key)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs sm:text-sm font-semibold whitespace-nowrap transition ${
                active
                  ? 'bg-[#1F2A44] text-[#FAF7F2] shadow-sm'
                  : 'bg-white text-[#1F2A44] border border-[#1F2A44]/15 hover:border-[#E8B530]'
              }`}
            >
              <tab.icon size={15} className={active ? 'text-[#E8B530]' : 'text-[#1F2A44]/70'} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab 1: Personal Info */}
      {currentTab === 'profile' && (
        <div className="bg-white border border-[#1F2A44]/10 rounded-2xl p-5 sm:p-7 shadow-sm">
          <form onSubmit={saveProfile} className="space-y-6">
            <div className="flex items-center gap-4 border-b border-[#1F2A44]/10 pb-5">
              <div className="h-16 w-16 rounded-full bg-[#E8B530] text-[#1F2A44] font-bold text-2xl grid place-items-center uppercase shadow">
                {(profile.name || 'C').slice(0, 1)}
              </div>
              <div>
                <h3 className="font-semibold text-lg text-[#1F2A44]">{profile.name || 'User Profile'}</h3>
                <p className="text-xs text-[#1F2A44]/60">Verified Flipkart & FOSEN Customer</p>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs uppercase font-bold text-[#1F2A44]/70 mb-2">Full Name *</label>
                <input
                  required
                  type="text"
                  value={profile.name}
                  onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                  placeholder="Enter full name"
                  className="w-full border border-[#1F2A44]/20 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#E8B530]"
                />
              </div>

              <div>
                <label className="block text-xs uppercase font-bold text-[#1F2A44]/70 mb-2">Gender</label>
                <div className="flex items-center gap-4 pt-2">
                  {['Male', 'Female'].map((g) => (
                    <label key={g} className="flex items-center gap-2 text-sm font-medium text-[#1F2A44] cursor-pointer">
                      <input
                        type="radio"
                        name="gender"
                        value={g}
                        checked={profile.gender === g}
                        onChange={() => setProfile({ ...profile, gender: g })}
                        className="accent-[#E8B530]"
                      />
                      {g}
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs uppercase font-bold text-[#1F2A44]/70">Email Address *</label>
                  <span className="text-[11px] text-emerald-700 font-semibold flex items-center gap-1">
                    <CheckCircle2 size={12} /> Verified
                  </span>
                </div>
                <input
                  required
                  type="email"
                  value={profile.email}
                  onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                  placeholder="Email address"
                  className="w-full border border-[#1F2A44]/20 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#E8B530]"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs uppercase font-bold text-[#1F2A44]/70">Mobile Number</label>
                  <span className="text-[11px] text-emerald-700 font-semibold flex items-center gap-1">
                    <CheckCircle2 size={12} /> Verified
                  </span>
                </div>
                <input
                  type="tel"
                  value={profile.phone}
                  onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                  placeholder="Phone number"
                  className="w-full border border-[#1F2A44]/20 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#E8B530]"
                />
              </div>

              <div className="sm:col-span-2">
                <label className="block text-xs uppercase font-bold text-[#1F2A44]/70 mb-2">Profile Image URL</label>
                <input
                  type="url"
                  value={profile.profileImage}
                  onChange={(e) => setProfile({ ...profile, profileImage: e.target.value })}
                  placeholder="https://..."
                  className="w-full border border-[#1F2A44]/20 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#E8B530]"
                />
              </div>
            </div>

            <div className="pt-4 border-t border-[#1F2A44]/10 flex items-center justify-between">
              <button
                type="submit"
                className="bg-[#1F2A44] hover:bg-[#2F3B58] text-[#FAF7F2] font-semibold px-6 py-3 rounded-xl uppercase tracking-wider text-xs sm:text-sm inline-flex items-center gap-2 transition"
              >
                <Save size={16} /> Save Changes
              </button>
              {message && <p className="text-xs font-semibold text-emerald-700">{message}</p>}
            </div>
          </form>
        </div>
      )}

      {/* Tab 2: Manage Addresses */}
      {currentTab === 'addresses' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between bg-white border border-[#1F2A44]/10 rounded-2xl p-4 sm:p-5 shadow-sm">
            <h3 className="font-semibold text-base text-[#1F2A44]">Saved Addresses ({addresses.length})</h3>
            <button
              onClick={() => setShowAddAddress(!showAddAddress)}
              className="bg-[#E8B530] hover:bg-[#D4A220] text-[#1F2A44] font-bold px-4 py-2 rounded-xl text-xs uppercase tracking-wider inline-flex items-center gap-1.5 transition"
            >
              <Plus size={16} /> Add New Address
            </button>
          </div>

          {/* Add Address Form */}
          {showAddAddress && (
            <div className="bg-white border border-[#E8B530] rounded-2xl p-5 sm:p-6 shadow-md">
              <h4 className="font-bold text-sm uppercase tracking-wider text-[#1F2A44] mb-4">Add Delivery Address</h4>
              <form onSubmit={saveAddress} className="grid sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className="block text-xs uppercase font-bold text-[#1F2A44]/70 mb-1">Full Address / Flat / Building *</label>
                  <input
                    required
                    value={addressForm.fullAddress}
                    onChange={(e) => setAddressForm({ ...addressForm, fullAddress: e.target.value })}
                    placeholder="House No., Building Name, Street, Locality"
                    className="w-full border border-[#1F2A44]/20 rounded-xl px-4 py-2.5 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase font-bold text-[#1F2A44]/70 mb-1">City *</label>
                  <input
                    required
                    value={addressForm.city}
                    onChange={(e) => setAddressForm({ ...addressForm, city: e.target.value })}
                    placeholder="City"
                    className="w-full border border-[#1F2A44]/20 rounded-xl px-4 py-2.5 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase font-bold text-[#1F2A44]/70 mb-1">State *</label>
                  <input
                    required
                    value={addressForm.state}
                    onChange={(e) => setAddressForm({ ...addressForm, state: e.target.value })}
                    placeholder="State"
                    className="w-full border border-[#1F2A44]/20 rounded-xl px-4 py-2.5 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase font-bold text-[#1F2A44]/70 mb-1">Pincode *</label>
                  <input
                    required
                    value={addressForm.pincode}
                    onChange={(e) => setAddressForm({ ...addressForm, pincode: e.target.value })}
                    placeholder="6-digit Pincode"
                    className="w-full border border-[#1F2A44]/20 rounded-xl px-4 py-2.5 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase font-bold text-[#1F2A44]/70 mb-1">Phone Number *</label>
                  <input
                    required
                    value={addressForm.phone}
                    onChange={(e) => setAddressForm({ ...addressForm, phone: e.target.value })}
                    placeholder="Contact Phone Number"
                    className="w-full border border-[#1F2A44]/20 rounded-xl px-4 py-2.5 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase font-bold text-[#1F2A44]/70 mb-1">Landmark (Optional)</label>
                  <input
                    value={addressForm.landmark}
                    onChange={(e) => setAddressForm({ ...addressForm, landmark: e.target.value })}
                    placeholder="Nearby landmark"
                    className="w-full border border-[#1F2A44]/20 rounded-xl px-4 py-2.5 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase font-bold text-[#1F2A44]/70 mb-2">Address Type</label>
                  <div className="flex gap-4">
                    {['HOME', 'WORK'].map((t) => (
                      <button
                        type="button"
                        key={t}
                        onClick={() => setAddressForm({ ...addressForm, type: t })}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold ${
                          addressForm.type === t ? 'bg-[#1F2A44] text-[#FAF7F2]' : 'border border-[#1F2A44]/20 text-[#1F2A44]'
                        }`}
                      >
                        {t === 'HOME' ? <Home size={13} /> : <Briefcase size={13} />}
                        {t}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="sm:col-span-2 flex justify-end gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowAddAddress(false)}
                    className="px-4 py-2 border border-[#1F2A44]/20 text-[#1F2A44] text-xs font-semibold rounded-xl"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-[#1F2A44] text-[#FAF7F2] text-xs font-bold uppercase rounded-xl"
                  >
                    Save Address
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Address Cards Grid */}
          <div className="grid sm:grid-cols-2 gap-4">
            {addresses.map((addr) => (
              <div key={addr._id} className="bg-white border border-[#1F2A44]/10 rounded-2xl p-5 shadow-sm relative">
                <div className="flex items-center justify-between mb-3">
                  <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2.5 py-1 rounded-md uppercase tracking-wider ${
                    addr.type === 'WORK' ? 'bg-blue-100 text-blue-800' : 'bg-amber-100 text-amber-900'
                  }`}>
                    {addr.type === 'WORK' ? <Briefcase size={12} /> : <Home size={12} />}
                    {addr.type || 'HOME'}
                  </span>
                  {addr.isDefault && (
                    <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                      DEFAULT ADDRESS
                    </span>
                  )}
                </div>

                <p className="font-semibold text-[#1F2A44] text-sm leading-snug">{addr.fullAddress}</p>
                <p className="text-xs text-[#1F2A44]/70 mt-1">{addr.city}, {addr.state} - {addr.pincode}</p>
                {addr.landmark && <p className="text-xs text-[#1F2A44]/50">Landmark: {addr.landmark}</p>}
                <p className="text-xs font-medium text-[#1F2A44] mt-2">Phone: {addr.phone || user.phone || '9876543210'}</p>

                <div className="mt-4 pt-3 border-t border-[#1F2A44]/10 flex items-center justify-end gap-3">
                  <button
                    onClick={() => deleteAddress(addr._id)}
                    className="text-xs text-red-600 hover:text-red-800 font-semibold inline-flex items-center gap-1"
                  >
                    <Trash2 size={13} /> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tab 3: Wishlist */}
      {currentTab === 'wishlist' && (
        <div className="space-y-4">
          <div className="bg-white border border-[#1F2A44]/10 rounded-2xl p-4 sm:p-5 shadow-sm">
            <h3 className="font-semibold text-base text-[#1F2A44]">My Wishlist ({wishlist.length} Items)</h3>
          </div>

          {wishlist.length === 0 ? (
            <div className="bg-white border border-[#1F2A44]/10 rounded-2xl p-8 text-center text-[#1F2A44]/60">
              Your wishlist is empty. Browse shop to add favorite furniture pieces!
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-4">
              {wishlist.map((item) => (
                <div key={item.id} className="bg-white border border-[#1F2A44]/10 rounded-2xl p-4 flex gap-4 shadow-sm relative group">
                  <img src={item.image} alt={item.name} className="w-24 h-24 object-cover rounded-xl bg-[#F3EFE8] flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-sm text-[#1F2A44] line-clamp-2 leading-snug">{item.name}</h4>
                    <div className="flex items-baseline gap-2 mt-2">
                      <span className="font-bold text-sm text-[#1F2A44]">{fmt(item.price)}</span>
                      {item.marketPrice > item.price && (
                        <span className="text-xs text-[#1F2A44]/40 line-through">{fmt(item.marketPrice)}</span>
                      )}
                      <span className="text-[11px] text-green-700 font-bold">{item.discount}% OFF</span>
                    </div>

                    <div className="mt-3 flex items-center gap-2">
                      <button
                        onClick={() => { addToCart(item); toast({ title: 'Moved to Cart', description: item.name }); }}
                        className="bg-[#1F2A44] hover:bg-[#2F3B58] text-[#FAF7F2] text-xs font-semibold px-3 py-1.5 rounded-lg inline-flex items-center gap-1"
                      >
                        <ShoppingBag size={13} /> Add to Cart
                      </button>
                      <button
                        onClick={() => setWishlist(wishlist.filter((w) => w.id !== item.id))}
                        className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg"
                        title="Remove from wishlist"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Tab 4: Reviews */}
      {currentTab === 'reviews' && (
        <div className="space-y-4">
          <div className="bg-white border border-[#1F2A44]/10 rounded-2xl p-4 sm:p-5 shadow-sm">
            <h3 className="font-semibold text-base text-[#1F2A44]">My Ratings & Reviews ({sampleReviews.length})</h3>
          </div>

          <div className="space-y-3">
            {sampleReviews.map((r) => (
              <div key={r.id} className="bg-white border border-[#1F2A44]/10 rounded-2xl p-5 shadow-sm">
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold text-sm text-[#1F2A44]">{r.productName}</h4>
                  <span className="text-xs text-[#1F2A44]/50">{r.date}</span>
                </div>
                <div className="flex items-center gap-1 mt-2 text-amber-500">
                  {[...Array(r.rating)].map((_, i) => (
                    <Star key={i} size={14} className="fill-[#E8B530] text-[#E8B530]" />
                  ))}
                  <span className="text-xs font-bold text-[#1F2A44] ml-1">{r.rating}.0 / 5.0</span>
                </div>
                <p className="text-xs sm:text-sm text-[#1F2A44]/80 mt-2 leading-relaxed italic">“{r.comment}”</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tab 5: Coupons */}
      {currentTab === 'coupons' && (
        <div className="space-y-4">
          <div className="bg-white border border-[#1F2A44]/10 rounded-2xl p-4 sm:p-5 shadow-sm">
            <h3 className="font-semibold text-base text-[#1F2A44]">Available Coupons & Discount Offers</h3>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            {sampleCoupons.map((c) => (
              <div key={c.code} className="bg-white border-2 border-dashed border-[#E8B530] rounded-2xl p-5 shadow-sm relative">
                <span className="text-xs uppercase font-bold text-[#E8B530] bg-[#E8B530]/15 px-3 py-1 rounded-md">
                  {c.discount}
                </span>
                <h4 className="font-bold text-sm text-[#1F2A44] mt-3">{c.title}</h4>
                <p className="text-xs text-[#1F2A44]/60 mt-1">Min Order: {fmt(c.minSpend)} | Valid till {c.validTill}</p>

                <div className="mt-4 pt-3 border-t border-[#1F2A44]/10 flex items-center justify-between">
                  <code className="bg-[#FAF7F2] border border-[#1F2A44]/20 px-3 py-1 rounded-lg text-xs font-bold text-[#1F2A44] tracking-widest">
                    {c.code}
                  </code>
                  <button
                    onClick={() => copyCouponCode(c.code)}
                    className="bg-[#1F2A44] hover:bg-[#2F3B58] text-[#FAF7F2] px-3.5 py-1.5 rounded-lg text-xs font-semibold inline-flex items-center gap-1"
                  >
                    {copiedCode === c.code ? <Check size={13} className="text-emerald-400" /> : <Copy size={13} />}
                    {copiedCode === c.code ? 'Copied!' : 'Copy Code'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tab 6: PAN & GST */}
      {currentTab === 'pan' && (
        <div className="bg-white border border-[#1F2A44]/10 rounded-2xl p-5 sm:p-7 shadow-sm">
          <form onSubmit={savePanDetails} className="space-y-6">
            <div className="border-b border-[#1F2A44]/10 pb-4">
              <h3 className="font-semibold text-lg text-[#1F2A44]">PAN & GSTIN Billing Information</h3>
              <p className="text-xs text-[#1F2A44]/60 mt-1">Save tax information for business furniture purchases and GST tax invoice claims.</p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs uppercase font-bold text-[#1F2A44]/70 mb-1">PAN Card Number</label>
                <input
                  type="text"
                  maxLength={10}
                  value={panInfo.panNumber}
                  onChange={(e) => setPanInfo({ ...panInfo, panNumber: e.target.value.toUpperCase() })}
                  placeholder="e.g. ABCDE1234F"
                  className="w-full border border-[#1F2A44]/20 rounded-xl px-4 py-3 text-sm uppercase focus:outline-none focus:ring-2 focus:ring-[#E8B530]"
                />
              </div>

              <div>
                <label className="block text-xs uppercase font-bold text-[#1F2A44]/70 mb-1">Full Name on PAN Card</label>
                <input
                  type="text"
                  value={panInfo.fullNameOnPan}
                  onChange={(e) => setPanInfo({ ...panInfo, fullNameOnPan: e.target.value })}
                  placeholder="Name as printed on PAN card"
                  className="w-full border border-[#1F2A44]/20 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#E8B530]"
                />
              </div>

              <div>
                <label className="block text-xs uppercase font-bold text-[#1F2A44]/70 mb-1">GSTIN (Optional for GST Tax Invoice)</label>
                <input
                  type="text"
                  maxLength={15}
                  value={panInfo.gstin}
                  onChange={(e) => setPanInfo({ ...panInfo, gstin: e.target.value.toUpperCase() })}
                  placeholder="e.g. 29ABCDE1234F1Z5"
                  className="w-full border border-[#1F2A44]/20 rounded-xl px-4 py-3 text-sm uppercase focus:outline-none focus:ring-2 focus:ring-[#E8B530]"
                />
              </div>
            </div>

            <div className="pt-4 border-t border-[#1F2A44]/10 flex justify-end">
              <button
                type="submit"
                className="bg-[#1F2A44] hover:bg-[#2F3B58] text-[#FAF7F2] font-semibold px-6 py-3 rounded-xl uppercase tracking-wider text-xs sm:text-sm inline-flex items-center gap-2"
              >
                <Save size={16} /> Save Details
              </button>
            </div>
          </form>
        </div>
      )}
    </UserPanelShell>
  );
};

export default ProfilePage;
